import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import { useCan } from "../hooks/useCan";
import FieldsTab from "../components/settings/FieldsTab";
import TemplatesTab from "../components/settings/TemplatesTab";
import TestCaseTypesTab from "../components/customization/TestCaseTypesTab";
import ResultStatusesTab from "../components/customization/ResultStatusesTab";
import VariablesTab from "../components/customization/VariablesTab";
import ResultFieldsTab from "../components/customization/ResultFieldsTab";

const PlaceholderTab = ({
  title,
  message = "This tab is under construction.",
}) => (
  <div className="py-8 px-4 text-center text-text-secondary">
    <h3 className="text-lg font-medium text-text-primary mb-2">{title}</h3>
    <p>{message}</p>
  </div>
);

const CustomizationPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlClientId = searchParams.get("client_id");
  const storedClientId = getStoredClientId();
  const effectiveClientId = urlClientId || storedClientId || user?.client_id;

  const tabParam = searchParams.get("tab") || "case-fields";
  const [activeTab, setActiveTab] = useState(tabParam);

  const userRole = user?.role_name || user?.role;
  const normalizeRole = (r) =>
    r ? r.toLowerCase().replace(/[_\s-]/g, "-") : null;
  const normalizedRole = normalizeRole(userRole);
  const allowedRoles = [
    "chief-admin",
    "admin",
    "project-manager",
    "project_manager",
    "test-manager",
    "test_manager",
  ];
  const hasAllowedRole = allowedRoles.includes(normalizedRole);
  const { can: canViewCustomization } = useCan("customization", "view");
  const { can: canViewTemplate } = useCan("template", "view");
  const canAccess = hasAllowedRole || canViewCustomization || canViewTemplate;

  useEffect(() => {
    const t = searchParams.get("tab") || "case-fields";
    setActiveTab(t);
  }, [searchParams]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    const params = new URLSearchParams(searchParams);
    params.set("tab", value);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`,
    );
  };

  if (!canAccess) {
    return (
      <div className="p-6">
        <p className="text-text-secondary">
          You do not have access to Customization settings.
        </p>
      </div>
    );
  }

  const tabs = [
    { id: "case-fields", label: "Case Fields", component: FieldsTab },
    { id: "result-fields", label: "Result Fields", component: ResultFieldsTab },
    { id: "templates", label: "Templates", component: TemplatesTab },
    {
      id: "test-case-types",
      label: "Test Case Types",
      component: TestCaseTypesTab,
    },
    {
      id: "result-statuses",
      label: "Result Statuses",
      component: ResultStatusesTab,
    },
    { id: "variables", label: "Variables", component: VariablesTab },
  ];

  return (
    <div className="space-y-6" style={{ width: "calc(100% - 210px)" }}>
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Customization</h1>
        <p className="text-text-secondary mt-1">
          Configure case fields, result fields, templates, test case types,
          result statuses, and variables.
        </p>
      </div>

      <div className="card">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="border-b px-6 pt-6">
            <TabsList
              className="h-auto bg-transparent p-0 w-full justify-start gap-1 flex flex-wrap"
              style={{ overflow: "scroll" }}
            >
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#2E9E6F] data-[state=active]:text-[#2E9E6F] rounded-none px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors border-b-2 border-transparent"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="p-6">
            {tabs.map((tab) => {
              const Component = tab.component;
              return (
                <TabsContent key={tab.id} value={tab.id} className="mt-0">
                  {tab.id === "case-fields" ||
                  tab.id === "result-fields" ||
                  tab.id === "templates" ||
                  tab.id === "test-case-types" ||
                  tab.id === "result-statuses" ||
                  tab.id === "variables" ? (
                    <Component clientId={effectiveClientId} />
                  ) : (
                    <Component />
                  )}
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default CustomizationPage;
